/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';

// 导入页面组件

import CodeReaderPrivacyPolicyEn from './pages/codereader/en/CodeReaderPrivacyPolicyEn';
import CodeReaderPrivacyPolicyJa from './pages/codereader/ja/CodeReaderPrivacyPolicyJa';
import CodeReaderPrivacyPolicyZhCn from './pages/codereader/zh-cn/CodeReaderPrivacyPolicyZhCn';
import CodeReaderPrivacyPolicyKo from './pages/codereader/ko/CodeReaderPrivacyPolicyKo';
import CodeReaderPrivacyPolicyZhTw from './pages/codereader/zh-tw/CodeReaderPrivacyPolicyZhTw';
import IdPhotoPrivacyPolicyEn from './pages/idphoto/en/IdPhotoPrivacyPolicyEn';
import IdPhotoPrivacyPolicyJa from './pages/idphoto/ja/IdPhotoPrivacyPolicyJa';
import IdPhotoPrivacyPolicyZhCn from './pages/idphoto/zh-cn/IdPhotoPrivacyPolicyZhCn';
import IdPhotoPrivacyPolicyZhTw from './pages/idphoto/zh-tw/IdPhotoPrivacyPolicyZhTw';
import IdPhotoPrivacyPolicyKo from './pages/idphoto/ko/IdPhotoPrivacyPolicyKo';

// 定义应用信息的接口类型
interface AppInfo {
  name: string;         // 应用名称
  description: string;  // 应用描述
  icon: string;         // 应用图标路径
  appStoreUrl: string;  // App Store 链接
}

// 标题样式
const Title = styled('h1')`
  color: #1d1d1f;
  font-size: 2rem;
  margin-bottom: 2rem;
  text-align: center;
`;

// 全局容器
const GlobalContainer = styled('div')`
  min-height: 100vh;
  background-color: #f5f5f7;
  display: flex;
  flex-direction: column;
`;

// 主内容区域
const Container = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem 1rem;
`;

// 应用卡片容器
const AppsContainer = styled('div')`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  max-width: 900px;
  width: 100%;
`;

// 应用卡片
const AppCard = styled('div')`
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  border-radius: 20px;
  padding: 1.5rem;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  width: 200px;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
  }
`;

// 应用图标
const AppIcon = styled('div')`
  width: 48px;
  height: 48px;
  margin: 0 auto 1.2rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
  }
`;

// 应用标题
const AppTitle = styled('h3')`
  margin-bottom: 0.8rem;
  font-size: 0.8rem;
  color: #1d1d1f;
`;

// 应用描述
const AppDescription = styled('p')`
  color: #6e6e73;
  margin-bottom: 0.5rem;
  font-size: 0.6rem;
  height: 20px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
`;

// 下载按钮
const DownloadButton = styled('a')`
  display: inline-block;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  img {
    height: 30px;
  }
`;

// 页脚
const Footer = styled('footer')`
  width: 100%;
  text-align: center;
  padding: 1rem 0;
  color: #86868b;
  font-size: 0.9rem;
  background-color: #f5f5f7;
`;

// 应用数据
const apps: AppInfo[] = [
  {
    name: "証明写真 ID Photo",
    description: "ID photo production",
    icon: "/assets/idphoto_icon.png",
    appStoreUrl: "https://apps.apple.com/app/id6738624802"
  },
  {
    name: "QR Code Reader",
    description: "Quickly reads QR codes",
    icon: "/assets/codereader_icon.png",
    appStoreUrl: "https://apps.apple.com/app/id6739070413"
  },
 
];

// 主页组件
function Home() {
  const currentYear = new Date().getFullYear();

  return (
    <GlobalContainer>
      <Container>
        <Title>My Apps</Title>
        <AppsContainer>
          {apps.map((app, index) => (
            <AppCard key={index}>
              <AppIcon>
                <img src={app.icon} alt={app.name} />
              </AppIcon>
              <AppTitle>{app.name}</AppTitle>
              <AppDescription>{app.description}</AppDescription>
              <DownloadButton href={app.appStoreUrl} target="_blank" rel="noopener noreferrer">
                <img src="/assets/appstore_icon.svg" alt="Download on App Store" />
              </DownloadButton>
            </AppCard>
          ))}
        </AppsContainer>
      </Container>
      <Footer>
        <p>© {currentYear} linappworks. All rights reserved.</p>
        <p>Contact us: <a href="mailto:linappworks@gmail.com" style={{ color: 'inherit', textDecoration: 'underline' }}>linappworks@gmail.com</a></p>
      </Footer>
    </GlobalContainer>
  );
}

// 主应用组件
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/en/codereader/privacypolicy" element={<CodeReaderPrivacyPolicyEn />} />
        <Route path="/ja/codereader/privacypolicy" element={<CodeReaderPrivacyPolicyJa />} />
        <Route path="/zh-cn/codereader/privacypolicy" element={<CodeReaderPrivacyPolicyZhCn />} />
        <Route path="/zh-tw/codereader/privacypolicy" element={<CodeReaderPrivacyPolicyZhTw />} />
        <Route path="/ko/codereader/privacypolicy" element={<CodeReaderPrivacyPolicyKo />} />
        <Route path="/idphoto/privacypolicy/en" element={<IdPhotoPrivacyPolicyEn />} />
        <Route path="/idphoto/privacypolicy/ja" element={<IdPhotoPrivacyPolicyJa />} />
        <Route path="/idphoto/privacypolicy/zh-cn" element={<IdPhotoPrivacyPolicyZhCn />} />
        <Route path="/idphoto/privacypolicy/zh-tw" element={<IdPhotoPrivacyPolicyZhTw />} />
        <Route path="/idphoto/privacypolicy/ko" element={<IdPhotoPrivacyPolicyKo />} />
      </Routes>
    </Router>
  );
}

export default App;
